import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api-client';

import type { PasswordResetPreviewWire } from '../features/admin/users/types';

type Blocked =
  | { kind: 'not-found' }
  | { kind: 'not-usable'; reason: string }
  | { kind: 'error'; message: string };

export function resetBlockedCopy(b: Blocked): { title: string; hint: string } {
  if (b.kind === 'not-found') {
    return {
      title: 'Este link não é válido.',
      hint: 'Confira se veio completo. Se veio, peça um link novo.',
    };
  }
  if (b.kind === 'not-usable') {
    if (b.reason === 'used')
      return {
        title: 'Este link já foi usado.',
        hint: 'Entre com a senha que você criou. Se não foi você, peça um link novo agora.',
      };
    if (b.reason === 'expired')
      return { title: 'Este link expirou.', hint: 'Links de senha valem 1 hora. Peça um novo.' };
    return { title: 'Este link não pode mais ser usado.', hint: 'Peça um link novo.' };
  }
  return { title: 'Não foi possível abrir o link.', hint: b.message };
}

function toBlocked(err: unknown): Blocked {
  if (err instanceof ApiError) {
    if (err.status === 404) return { kind: 'not-found' };
    if (err.status === 410)
      return { kind: 'not-usable', reason: (err.body as { reason?: string }).reason ?? 'unknown' };
    return { kind: 'error', message: err.message };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Erro inesperado.' };
}

/** Página pública `/redefinir-senha/:secret` (Item 477) — cria a senha nova e manda para o login. */
export function RedefinirSenhaPage(): JSX.Element {
  const { secret = '' } = useParams<{ secret: string }>();
  const [preview, setPreview] = useState<PasswordResetPreviewWire | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Lume — Redefinir senha';
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .previewPasswordReset(secret)
      .then((p) => {
        if (alive) setPreview(p);
      })
      .catch((err: unknown) => {
        if (alive) setBlocked(toBlocked(err));
      });
    return () => {
      alive = false;
    };
  }, [secret]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As duas senhas não coincidem.');
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword({ token: secret, newPassword: password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410))
        setBlocked(toBlocked(err));
      else setError(err instanceof Error ? err.message : 'Erro inesperado.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-surface p-8 shadow-md border border-border-subtle">
        {blocked !== null && (
          <div className="space-y-3 text-center" role="alert">
            <h1 className="text-xl font-semibold tracking-tight text-text-primary">
              {resetBlockedCopy(blocked).title}
            </h1>
            <p className="text-sm text-text-secondary">{resetBlockedCopy(blocked).hint}</p>
            <Link to="/esqueci-senha" className="btn btn--secondary w-full">
              Pedir um link novo
            </Link>
          </div>
        )}

        {blocked === null && preview === null && (
          <p className="text-sm text-text-secondary text-center" role="status">
            Abrindo o link…
          </p>
        )}

        {blocked === null && preview !== null && done && (
          <div className="space-y-3 text-center" role="status">
            <h1 className="text-xl font-semibold tracking-tight text-text-primary">
              Senha redefinida.
            </h1>
            <p className="text-sm text-text-secondary">
              Entre com <strong>{preview.email}</strong> e a senha nova. Suas outras sessões foram
              encerradas.
            </p>
            <Link to="/login" className="btn btn--primary w-full">
              Entrar
            </Link>
          </div>
        )}

        {blocked === null && preview !== null && !done && (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-xl font-semibold tracking-tight text-text-primary">
                Criar uma senha nova
              </h1>
              <p className="text-sm text-text-secondary">
                <strong>{preview.tenantName}</strong> · {preview.email}
              </p>
            </div>
            <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-text-primary">Senha nova</span>
                <input
                  className="input w-full"
                  aria-label="Senha nova"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="text-xs text-text-secondary">Pelo menos 8 caracteres.</span>
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-text-primary">Repita a senha</span>
                <input
                  className="input w-full"
                  aria-label="Repita a senha"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </label>
              {error !== null && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
              <button type="submit" className="btn btn--primary w-full" disabled={submitting}>
                {submitting ? 'Salvando…' : 'Salvar senha nova'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
